import { AuthService } from '../services/AuthService';
import { User } from '../models/User';

export class LoginScreen {
  private container: HTMLElement;
  private authService: AuthService;
  private onLoginSuccess: (user: User) => void;
  private onSkip: () => void;
  
  constructor(authService: AuthService, onLoginSuccess: (user: User) => void, onSkip: () => void) {
    this.authService = authService;
    this.onLoginSuccess = onLoginSuccess;
    this.onSkip = onSkip;
    
    this.container = document.createElement('div');
    this.container.className = 'login-screen';
    this.render();
  }
  
  private render(): void {
    this.container.innerHTML = `
      <div class="login-card">
        <h1>チムドンミニ</h1>
        <p class="login-subtitle">ミニパズルゲーム</p>

        <div class="login-buttons">
          <div class="nickname-section">
            <input
              type="text"
              id="nickname-input"
              class="nickname-input"
              placeholder="ニックネーム（任意）"
              maxlength="12"
            />
            <button id="login-anonymous" class="login-button anonymous">
              <span class="button-icon">🎮</span>
              <span class="button-text">ゲストでプレイ</span>
            </button>
          </div>

          <button id="login-google" class="login-button google">
            <span class="button-icon">G</span>
            <span class="button-text">Googleでログイン</span>
          </button>

          <button id="login-twitter" class="login-button twitter">
            <span class="button-icon">𝕏</span>
            <span class="button-text">Xでログイン</span>
          </button>
        </div>

        <div class="login-info">
          <p>ゲストプレイでもランキング参加可能です</p>
          <p>Google/Xログインで記録を永続保存できます</p>
        </div>

        <div class="skip-login">
          <button id="skip-login" class="skip-button">
            ログインせずにプレイ（記録なし）
          </button>
        </div>

        <div id="login-error" class="login-error" style="display: none;"></div>
      </div>
    `;

    this.attachEventHandlers();
  }
  
  private attachEventHandlers(): void {
    const anonymousBtn = this.container.querySelector('#login-anonymous') as HTMLButtonElement;
    const googleBtn = this.container.querySelector('#login-google') as HTMLButtonElement;
    const twitterBtn = this.container.querySelector('#login-twitter') as HTMLButtonElement;
    const skipBtn = this.container.querySelector('#skip-login') as HTMLButtonElement;

    if (anonymousBtn) {
      anonymousBtn.addEventListener('click', () => this.handleAnonymousLogin());
    }

    if (googleBtn) {
      googleBtn.addEventListener('click', () => this.handleGoogleLogin());
    }

    if (twitterBtn) {
      twitterBtn.addEventListener('click', () => this.handleTwitterLogin());
    }

    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this.onSkip();
      });
    }
  }
  
  private async handleAnonymousLogin(): Promise<void> {
    const button = this.container.querySelector('#login-anonymous') as HTMLButtonElement;
    const nicknameInput = this.container.querySelector('#nickname-input') as HTMLInputElement;
    const errorDiv = this.container.querySelector('#login-error') as HTMLElement;

    try {
      button.disabled = true;
      button.innerHTML = '<span class="loading">ログイン中...</span>';
      errorDiv.style.display = 'none';

      // ニックネームを取得（空白をトリム）
      const nickname = nicknameInput.value.trim();

      const user = await this.authService.signInAnonymously(nickname || undefined);
      this.onLoginSuccess(user);

    } catch (error) {
      console.error('Anonymous login failed:', error);
      errorDiv.textContent = 'ログインに失敗しました。もう一度お試しください。';
      errorDiv.style.display = 'block';

      button.disabled = false;
      button.innerHTML = `
        <span class="button-icon">🎮</span>
        <span class="button-text">ゲストでプレイ</span>
      `;
    }
  }
  
  private async handleGoogleLogin(): Promise<void> {
    const button = this.container.querySelector('#login-google') as HTMLButtonElement;
    const errorDiv = this.container.querySelector('#login-error') as HTMLElement;

    try {
      button.disabled = true;
      button.innerHTML = '<span class="loading">ログイン中...</span>';
      errorDiv.style.display = 'none';

      const user = await this.authService.signInWithGoogle();
      this.onLoginSuccess(user);

    } catch (error: any) {
      console.error('Google login failed:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        errorDiv.textContent = 'ログインがキャンセルされました。';
      } else {
        errorDiv.textContent = 'Googleログインに失敗しました。もう一度お試しください。';
      }
      errorDiv.style.display = 'block';

      button.disabled = false;
      button.innerHTML = `
        <span class="button-icon">G</span>
        <span class="button-text">Googleでログイン</span>
      `;
    }
  }

  private async handleTwitterLogin(): Promise<void> {
    const button = this.container.querySelector('#login-twitter') as HTMLButtonElement;
    const errorDiv = this.container.querySelector('#login-error') as HTMLElement;

    try {
      button.disabled = true;
      button.innerHTML = '<span class="loading">ログイン中...</span>';
      errorDiv.style.display = 'none';

      const user = await this.authService.signInWithTwitter();
      this.onLoginSuccess(user);

    } catch (error: any) {
      console.error('Twitter login failed:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        errorDiv.textContent = 'ログインがキャンセルされました。';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorDiv.textContent = 'このドメインからのTwitterログインは許可されていません。';
      } else {
        errorDiv.textContent = 'Xログインに失敗しました。もう一度お試しください。';
      }
      errorDiv.style.display = 'block';

      button.disabled = false;
      button.innerHTML = `
        <span class="button-icon">𝕏</span>
        <span class="button-text">Xでログイン</span>
      `;
    }
  }
  
  public mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }
  
  public unmount(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}